import EventEmitter from 'eventemitter3';
import {
  AbstractWsConnector,
  CaseEventName,
  CaseEventType,
  CaseGroundTripEventType,
  CaseGroupEventType,
  CaseRouteEventType,
  WsCaseEventMessageContent,
  wsDefaultConnector,
  WsMessageCaseType,
  WsMessageGroundTripType,
  WsMessageRouteType,
} from '@/ws';

export class CaseNotifier {
  protected readonly eventEmitter = new EventEmitter();

  static getEventName(eventName: string | CaseEventType | CaseRouteEventType | CaseGroundTripEventType): CaseEventName;
  static getEventName(
    caseId: string,
    eventType: CaseEventType | CaseRouteEventType | CaseGroundTripEventType,
  ): CaseEventName;
  static getEventName(
    first: string | CaseEventType | CaseRouteEventType | CaseGroundTripEventType,
    eventType?: CaseEventType | CaseRouteEventType | CaseGroundTripEventType,
  ): CaseEventName {
    if (eventType) return `${first}:${eventType}` as const;
    return first;
  }

  constructor(protected readonly wsConnector: AbstractWsConnector) {
    this.wsConnector.on((type, content, userId) => {
      if (
        ![
          WsMessageCaseType.CASE_EVENT_CREATED,
          WsMessageCaseType.MAO_TRIP_UPDATE,
          WsMessageCaseType.ROUTE_CREATED_EVENT,
          WsMessageCaseType.LEGS_UPDATED_EVENT,
          WsMessageRouteType.RouteChanged,
          WsMessageRouteType.RouteChanged,
          WsMessageGroundTripType.GroundTripUpdated,
        ].includes(type as WsMessageCaseType | WsMessageRouteType | WsMessageGroundTripType)
      ) {
        return;
      }

      const currentUserId = localStorage.getItem('userId');
      const messageContent = content as WsCaseEventMessageContent;
      const asyncEventTypes = [
        CaseEventType.CaseQuoteCreated,
        CaseEventType.CaseQuoteRejectedByTxCenter,
        CaseEventType.CaseQuoteSentToTxCenter,
        CaseEventType.CaseQuoteApprovedByTxCenter,
        CaseEventType.CaseReviewUpdated,
        CaseEventType.EtaUpdated,
        CaseEventType.LogisticsCreated,
      ];
      const isAsyncEventType =
        messageContent.eventType !== CaseRouteEventType.RouteChanged &&
        messageContent.eventType !== CaseGroundTripEventType.GroundTripUpdated &&
        asyncEventTypes.includes(messageContent.eventType);

      if (isAsyncEventType || currentUserId !== userId) {
        this.handleCaseEventCreated(messageContent);
      }
    });
  }

  protected handleCaseEventCreated(message: WsCaseEventMessageContent) {
    this.eventEmitter.emit(CaseNotifier.getEventName(message.caseId), message);
    this.eventEmitter.emit(CaseNotifier.getEventName(message.eventType), message);
    this.eventEmitter.emit(CaseNotifier.getEventName(message.caseId, message.eventType), message);
  }

  protected joinCaseListener(eventNames: CaseEventName[]) {
    const caseEventTypes = Object.values(CaseEventType);
    const invalidEventNames = eventNames.filter((eventName) => caseEventTypes.some((type) => eventName.includes(type)));

    if (invalidEventNames.length > 0) {
      console.error(
        `JoinCaseGroup(s) is available only for case IDs! The following event names are invalid: ${invalidEventNames.join(', ')}. WebSockets will not work properly!`,
      );
    }

    if (eventNames.length === 1) {
      this.wsConnector.invoke(CaseGroupEventType.JoinCaseGroup, eventNames[0]);
      return;
    }

    this.wsConnector.invoke(CaseGroupEventType.JoinCaseGroups, eventNames);
  }

  protected leaveCaseListener(eventNames: CaseEventName[]) {
    if (eventNames.length === 1) {
      this.wsConnector.invoke(CaseGroupEventType.LeaveCaseGroup, eventNames[0]);
      return;
    }
    this.wsConnector.invoke(CaseGroupEventType.LeaveCaseGroups, eventNames);
  }

  once(eventName: CaseEventName, handler: (message: WsCaseEventMessageContent) => void) {
    this.joinCaseListener([eventName]);
    this.eventEmitter.once(eventName, handler);
    return () => this.off([eventName], handler, true);
  }

  on(eventNames: CaseEventName[], handler: (message: WsCaseEventMessageContent) => void) {
    this.joinCaseListener(eventNames);
    eventNames.forEach((eventName) => {
      this.eventEmitter.on(eventName, handler);
    });
    return () => this.off(eventNames, handler);
  }

  off(eventNames: CaseEventName[], handler?: (message: WsCaseEventMessageContent) => void, once = false) {
    this.leaveCaseListener(eventNames);
    eventNames.forEach((eventName) => {
      this.eventEmitter.off(eventName, handler, null, once);
    });
  }
}

export const caseNotifier = new CaseNotifier(wsDefaultConnector);
